import {
  PENALTY_INPUTS,
  REF_EVENT_INPUTS,
  TEventTypes,
  TOUCHBACK_YARDLINE,
  TPlayDesigns,
  TPlayResultTypes,
  YARDAGE_SELECTOR_BUTTONS,
} from '@/constants';
import {
  IMachineContext,
  IMachineContextCurrentPlay,
  ITwoRowGenericSelectorInp,
  TColors,
  TKickoffEvents,
  TMachineEvents,
  TMetaFuncInput,
  TPlayResults,
  TPlays,
  TRefEventsActions,
  TTeamSide,
} from '@/types';
import { refEventValue } from '@/utilities';
import { pushResultType } from '@/workflow/utils';

export const onKickoffStarted = ({ send, payload, generalState, playFlowState, context }: TMetaFuncInput) => {
  if (payload.value === TPlays.REF) {
    playFlowState.pushEvent(
      {
        eventTypeId: TEventTypes.REF_EVENT,
        value: refEventValue(playFlowState.driveNumber, playFlowState.drivePlayNumber),
      },
      generalState.currentGame.gameId
    );

    send(TKickoffEvents.REF_EVENT, {
      nextStateInput: {
        ...REF_EVENT_INPUTS,
        backTo: TRefEventsActions.BACK_TO_START_KICKOFF,
      } as ITwoRowGenericSelectorInp,
      ctxUpdate: {
        currentPlay: {
          ...context.currentPlay,
          playDesignId: TPlayDesigns.KICKOFF,
          resultType: [],
        },
      } as IMachineContext,
    });
  }

  let playDesignId: number;
  if (payload.value === TPlays.ONSIDE) {
    playFlowState.pushEvent(
      { eventTypeId: TEventTypes.PLAY_DESIGN, value: TPlayDesigns.ONSIDE },
      generalState.currentGame.gameId
    );
    playDesignId = TPlayDesigns.ONSIDE;
  } else {
    playFlowState.pushEvent(
      { eventTypeId: TEventTypes.PLAY_DESIGN, value: TPlayDesigns.KICKOFF },
      generalState.currentGame.gameId
    );
    playDesignId = TPlayDesigns.KICKOFF;
  }

  send(TKickoffEvents.REGULAR_KICK, {
    nextStateInput: {
      topJustify: 'start',
      topOptions: [
        {
          text: 'KICKOFF KICKED!',
          value: '',
          color: TColors.GREEN,
          col: 12,
        },
      ],
      bottomJustify: 'space-between',
      bottomOptions: [
        {
          text: TPlays.ONSIDE,
          value: TPlays.ONSIDE,
          color: TColors.BLUE,
          isToggle: true,
          toggleInitialValue: playDesignId === TPlayDesigns.ONSIDE,
          col: 4,
        },
        {
          text: TPlays.REF,
          value: TPlays.REF,
          color: TColors.YELLOW,
          col: 4,
        },
      ],
    } as ITwoRowGenericSelectorInp,
    ctxUpdate: {
      currentPlay: {
        ...context.currentPlay,
        playDesignId,
        resultType: [],
        options: {
          ...context.currentPlay?.options,
          isKickoffOrPunt: true,
        },
      },
    } as IMachineContext,
  });
};

export const onKickoffKicked = ({ send, payload, generalState, playFlowState, context }: TMetaFuncInput) => {
  if (payload.value === TPlays.REF) {
    playFlowState.pushEvent(
      {
        eventTypeId: TEventTypes.REF_EVENT,
        value: refEventValue(playFlowState.driveNumber, playFlowState.drivePlayNumber),
      },
      generalState.currentGame.gameId
    );

    send(TKickoffEvents.REF_EVENT, {
      nextStateInput: {
        ...REF_EVENT_INPUTS,
        backTo: TRefEventsActions.BACK_TO_KICKOFF_KICKED,
      } as ITwoRowGenericSelectorInp,
    });
  } else if (payload.toggles && payload.toggles[TPlays.ONSIDE]) {
    playFlowState.pushEvent(
      { eventTypeId: TEventTypes.KICKOFF_KICKED, value: true },
      generalState.currentGame.gameId
    );
    send(TKickoffEvents.ONSIDE_KICK, {
      nextStateInput: {
        topOptions: [
          {
            text: 'KICKING TEAM RECOVERS',
            value: TPlayResults.ONSIDE_RECOVER,
            col: 6,
          },
          {
            text: 'RECEIVING TEAM RECOVERS',
            value: TPlayResults.RETURN,
            col: 6,
          },
        ],
        topJustify: 'start',
        bottomJustify: 'space-between',
        bottomOptions: [
          {
            text: TPlayResults.OUT_OF_BOUNDS,
            value: TPlayResults.OUT_OF_BOUNDS,
            col: 5,
          },
          {
            text: TPlays.PENALTY_ALERT,
            value: TPlays.PENALTY_ALERT,
            color: TColors.ORANGE,
            isToggle: true,
            col: 5,
          },
        ],
      } as ITwoRowGenericSelectorInp,
      ctxUpdate: {
        currentPlay: {
          ...context.currentPlay,
          playDesignId: TPlayDesigns.ONSIDE,
          options: {
            isKickoffSnapped: true,
          },
        },
      } as IMachineContext,
    });
  } else {
    playFlowState.pushEvent(
      { eventTypeId: TEventTypes.KICKOFF_KICKED, value: true },
      generalState.currentGame.gameId
    );

    send(TKickoffEvents.REGULAR_KICK, {
      nextStateInput: {
        topJustify: 'start',
        topOptions: [
          {
            text: TPlayResults.TOUCHBACK,
            value: TPlayResults.TOUCHBACK,
            col: 6,
          },
          {
            text: TPlayResults.RETURN,
            value: TPlayResults.RETURN,
            col: 6,
          },
        ],
        bottomJustify: 'space-between',
        bottomOptions: [
          {
            text: TPlayResults.OUT_OF_BOUNDS,
            value: TPlayResults.OUT_OF_BOUNDS,
            col: 5,
          },
          {
            text: TPlays.PENALTY_ALERT,
            value: TPlays.PENALTY_ALERT,
            color: TColors.ORANGE,
            isToggle: true,
            col: 5,
          },
        ],
      } as ITwoRowGenericSelectorInp,
      ctxUpdate: {
        currentPlay: {
          ...context.currentPlay,
          playDesignId: TPlayDesigns.KICKOFF,
          options: {
            isKickoffSnapped: true,
          },
        },
      } as IMachineContext,
    });
  }
};

export const onKickoffResult = ({ send, payload, generalState, playFlowState, context }: TMetaFuncInput) => {
  if (payload.toggles[TPlays.PENALTY_ALERT]) {
    playFlowState.pushEvent(
      {
        eventTypeId: TEventTypes.REF_EVENT,
        value: refEventValue(playFlowState.driveNumber, playFlowState.drivePlayNumber),
      },
      generalState.currentGame.gameId
    );

    send(TKickoffEvents.PENALTY, {
      ctxUpdate: {},
      nextStateInput: {
        ...PENALTY_INPUTS(
          playFlowState.currentTeamRoles.offense,
          generalState.teams[playFlowState.currentTeamRoles.offense].logoUrl,
          playFlowState.currentTeamRoles.defense,
          generalState.teams[playFlowState.currentTeamRoles.defense].logoUrl
        ),
        backTo: TRefEventsActions.BACK_TO_KICKOFF_RESULT,
      } as ITwoRowGenericSelectorInp,
    });
  } else if (payload.value === TPlayResults.TOUCHBACK) {
    const driveDirection =
      playFlowState.teams.right === playFlowState.presentStateBasedOnCurrentMode.activeTeam
        ? TTeamSide.RIGHT
        : TTeamSide.LEFT;
    send(TKickoffEvents.KICKOFF_COMPLETED, {
      ctxUpdate: {
        currentPlay: {
          ...context.currentPlay,
          resultType: pushResultType(context, TPlayResultTypes.TOUCHBACK),
          postPlayYardLine: TOUCHBACK_YARDLINE,
          postPlaySide: driveDirection === TTeamSide.LEFT ? TTeamSide.RIGHT : TTeamSide.LEFT,
          driveDirection,
        },
      },
      nextStateInput: {
        backTo: TRefEventsActions.BACK_TO_KICKOFF_RESULT,
      },
    });
  } else {
    send(TKickoffEvents.KICK_RESULT, {
      ctxUpdate: {
        currentPlay: {
          ...context.currentPlay,
          options: {
            ...context.currentPlay?.options,
            isOnsideRecover: payload.value === TPlayResults.ONSIDE_RECOVER,
          },
        },
      } as IMachineContext,
      nextStateInput: {
        buttons: YARDAGE_SELECTOR_BUTTONS(),
        sliderLabels: {
          left: generalState.teams[playFlowState.teams.left].abbreviation,
          right: generalState.teams[playFlowState.teams.right].abbreviation,
        },
        backTo: TMachineEvents.BACK,
      },
    });
  }
};
